import * as React from 'react'
import classNames from 'classnames'
import { useSearchParams } from 'react-router-dom'

import { displayReadablePassageTime } from '../utils/date.utils'
import { DisplayPassage, StatusLabel, getStatusLabels } from '../data/mappers'
import { ServerTimeContext } from '../contexts/serverTime.context'
import {
  getDisplayTimeClasses,
  getPassageTitleClasses,
  getPassageTitleWrapperClasses,
  getVignetteClasses,
} from './Passage.styles'
import { isAfter } from 'date-fns'

const liClasses = classNames(
  'flex flex-row h-full items-center justify-center px-5 gap-4',
  'even:bg-neutral-200 even:dark:bg-primary-grey text-primary-grey dark:text-neutral-100 font-flanders',
  'border-0 border-b border-solid border-neutral-300 dark:border-neutral-550 last:border-0',
)

function DisplayStatusLabels({
  statusLabels,
}: {
  statusLabels: StatusLabel[]
}) {
  if (!statusLabels.length) {
    return null
  }

  const [showIndex, setShowIndex] = React.useState(0)
  React.useEffect(() => {
    const toggleInterval = window.setInterval(() => {
      setShowIndex((showIndex) =>
        showIndex < statusLabels.length - 1 ? showIndex + 1 : 0,
      )
    }, 3_000)

    return function cleanup() {
      return window.clearInterval(toggleInterval)
    }
  })

  return (
    <div className="flex gap-2">
      {statusLabels.map((statusLabel, index) => {
        const { text, textColor, backgroundColor } = statusLabel
        const labelClasses = classNames(
          'rounded-lg text-[40px]',
          textColor,
          backgroundColor,
          {
            'sr-only': index !== showIndex, // dynamically hide/show label
          },
        )
        return (
          <span className={labelClasses} key={text}>
            {text}
          </span>
        )
      })}
    </div>
  )
}

export default function Passage({ passage }: { passage: DisplayPassage }) {
  const [searchParams] = useSearchParams()
  const debug = searchParams.get('debug')
  const { getCalibratedDateTime } = React.useContext(ServerTimeContext)

  const passageTime = new Date(
    passage.realtimeArrivalEpoch ||
      passage.plannedArrivalEpoch ||
      passage.realtimeDepartureEpoch ||
      passage.plannedDepartureEpoch,
  )
  const theoreticTime = new Date(
    passage.plannedArrivalEpoch || passage.plannedDepartureEpoch,
  )
  const theoreticHours = theoreticTime.getHours()
  const theoreticMinutes = theoreticTime.getMinutes()
  const theoreticDisplayTime = [
    String(theoreticHours).padStart(2, '0'),
    String(theoreticMinutes).padStart(2, '0'),
  ].join(':')

  const showTheoreticTime =
    passage.isDeparted || passage.isDeleted || passage.isNotServed

  const serverTime = getCalibratedDateTime()
  const hideTimeForPassage =
    passage.isDeparted && isAfter(serverTime, theoreticTime)

  const statusLabels = getStatusLabels(passage)
  const hasStatusLabels = statusLabels.length > 0

  const debugProps = debug ? { onClick: () => console.log(passage) } : {}
  return (
    <li className={liClasses} {...debugProps}>
      <span
        className={getVignetteClasses(passage)}
        style={{
          backgroundColor: passage.line.backgroundColor,
          borderColor: passage.line.backgroundColorBorder,
          borderWidth: '5px',
        }}
      >
        <span style={{ color: passage.line.color }}>
          {passage.line.publicNumber}
        </span>
      </span>
      <div className={getPassageTitleWrapperClasses(hasStatusLabels)}>
        <span className={getPassageTitleClasses(passage)}>
          {passage.destination}
        </span>
        {hasStatusLabels && <DisplayStatusLabels statusLabels={statusLabels} />}
      </div>
      <span
        className={getDisplayTimeClasses(passage, hideTimeForPassage)}
        title={debug ? passage.id : ''}
      >
        {showTheoreticTime
          ? theoreticDisplayTime
          : displayReadablePassageTime(passageTime, serverTime)}
      </span>
    </li>
  )
}
